import { useState, useEffect } from "react"
import { Input } from "../../ui/input"
import { Button } from "../../ui/button"
import { Checkbox } from "../../ui/checkbox"
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group"
import { Label } from "../../ui/label"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table"
import { Trash2, Plus, Settings } from "lucide-react"
import { API_ENDPOINTS } from "../../../constants/config"
import toast from "react-hot-toast"

function LateFeeConfigDialog({ classId, className, quarter, open, onOpenChange, onSave }) {
  const [settingsCalculationType, setSettingsCalculationType] = useState(null)
  const [globalLateFeePolicy, setGlobalLateFeePolicy] = useState(null)
  const [isLoadingStatus, setIsLoadingStatus] = useState(false)
  const [config, setConfig] = useState({
    isEnabled: false,
    calculationType: "per_day",
    dailyLateFees: 0,
  })
  const [slabs, setSlabs] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showSlabForm, setShowSlabForm] = useState(false)
  const [editingSlabId, setEditingSlabId] = useState(null)
  const [applyToAllClasses, setApplyToAllClasses] = useState(false)
  const [allClasses, setAllClasses] = useState([])
  const [slabForm, setSlabForm] = useState({
    slabType: "flat",
    startDate: "",
    endDate: "",
    perDayAmount: "",
    flatAmount: "",
  })

  useEffect(() => {
    if (open) fetchAllClasses()
  }, [open])

  useEffect(() => {
    if (open && classId && quarter) {
      setApplyToAllClasses(false)
      setIsLoadingStatus(true)
      fetchGlobalLateFeeSettings().then((type) => {
        fetchLateFeeConfig(type).finally(() => setIsLoadingStatus(false))
      })
    } else if (open && (!classId || !quarter)) {
      setConfig({
        isEnabled: false,
        calculationType: "per_day",
        dailyLateFees: 0,
      })
      setSlabs([])
      setGlobalLateFeePolicy(null)
    }
  }, [open, classId, quarter])

  const fetchGlobalLateFeeSettings = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.lateFeesGet, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        credentials: "include",
      })
      if (response.ok) {
        const result = await response.json()
        const data = result.data || result
        const type = data.calculationType === "flat_by_slab" ? "flat_by_slab" : "per_day"
        setSettingsCalculationType(type)
        setGlobalLateFeePolicy({
          isEnabled: data.isEnabled || false,
          calculationType: type,
          dailyLateFees: data.dailyLateFees || 0,
          slabs: data.slabs || [],
        })
        return type
      }
    } catch (err) {}
    setSettingsCalculationType("per_day")
    setGlobalLateFeePolicy({ isEnabled: false, calculationType: "per_day", dailyLateFees: 0, slabs: [] })
    return "per_day"
  }

  const describePolicyAmount = (policy) => {
    if (!policy) return ""
    if (policy.calculationType === "flat_by_slab") {
      return "slab-based rates"
    }
    return `₹${policy.dailyLateFees || 0}/day`
  }

  const fetchAllClasses = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.classes, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
      })
      
      if (response.ok) {
        const result = await response.json()
        setAllClasses(result.data?.classes || [])
      }
    } catch (err) {
      console.error("Error fetching classes:", err)
    }
  }

  const fetchLateFeeConfig = async (settingsType) => {
    try {
      const response = await fetch(API_ENDPOINTS.lateFeeConfig(classId, quarter), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
      })
      if (!response.ok) throw new Error("Failed to fetch late fee config")
      const result = await response.json()
      const data = result.data || result
      const type = settingsType ?? data.calculationType ?? "per_day"
      setConfig({
        isEnabled: data.isEnabled || false,
        calculationType: type,
        dailyLateFees: data.dailyLateFees || 0,
      })
      setSlabs(data.slabs || [])
    } catch (err) {
      console.error("Error fetching late fee config:", err)
    }
  }

  const handleSave = async () => {
    if (!quarter) {
      toast.error("Missing quarter information")
      return
    }

    if (applyToAllClasses && allClasses.length === 0) {
      toast.error("No classes found to apply configuration")
      return
    }

    if (applyToAllClasses && !classId) {
      toast.error("Please select a class first or uncheck 'Apply to all classes'")
      return
    }

    setIsLoading(true)
    try {
      const cleanSlabs = config.calculationType === "flat_by_slab" 
        ? slabs.map(slab => {
            const cleanSlab = {
              slabType: slab.slabType,
              isActive: slab.isActive !== undefined ? slab.isActive : true,
            }
            
            // Convert dates to ISO strings for JSON serialization
            if (slab.startDate) {
              cleanSlab.startDate = slab.startDate instanceof Date 
                ? slab.startDate.toISOString() 
                : new Date(slab.startDate).toISOString()
            }
            if (slab.endDate) {
              cleanSlab.endDate = slab.endDate instanceof Date 
                ? slab.endDate.toISOString() 
                : new Date(slab.endDate).toISOString()
            } else if (slab.slabType === "flat") {
              cleanSlab.endDate = null
            }
            if (slab.slabType === "flat") {
              cleanSlab.flatAmount = Number(slab.flatAmount) || 0
            } else if (slab.slabType === "per_day") {
              cleanSlab.perDayAmount = Number(slab.perDayAmount) || 0
            }
            if (slab._id && typeof slab._id === 'string' && !slab._id.startsWith('temp-') && /^[0-9a-fA-F]{24}$/.test(slab._id)) {
              cleanSlab._id = slab._id
            }
            
            return cleanSlab
          })
        : []

      if (applyToAllClasses) {
        const classIds = allClasses.map(cls => cls._id)
        
        const response = await fetch(API_ENDPOINTS.lateFeeConfigsBulk, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          credentials: "include",
          body: JSON.stringify({
            classIds,
            quarter,
            ...config,
            slabs: cleanSlabs,
          }),
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || "Failed to save late fee config for all classes")
        }

        const result = await response.json()
        toast.success(result.message || `Late fee configuration applied to ${classIds.length} classes successfully`)
      } else {
        const response = await fetch(API_ENDPOINTS.lateFeeConfigs, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          credentials: "include",
          body: JSON.stringify({
            classId,
            quarter,
            ...config,
            slabs: cleanSlabs,
          }),
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || "Failed to save late fee config")
        }

        toast.success("Late fee configuration saved successfully")
      }

      if (onSave) onSave()
      onOpenChange(false)
      setApplyToAllClasses(false)
    } catch (err) {
      const errorMsg = err.message || "Failed to save late fee config"
      toast.error(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddSlab = () => {
    setSlabForm({ 
      slabType: "flat",
      startDate: "",
      endDate: "",
      perDayAmount: "",
      flatAmount: "",
    })
    setEditingSlabId(null)
    setShowSlabForm(true)
  }

  const handleEditSlab = (slab) => {
    setSlabForm({
      slabType: slab.slabType || "flat",
      startDate: slab.startDate ? new Date(slab.startDate).toISOString().split('T')[0] : "",
      endDate: slab.endDate ? new Date(slab.endDate).toISOString().split('T')[0] : "",
      perDayAmount: slab.perDayAmount || "",
      flatAmount: slab.flatAmount || "",
    })
    setEditingSlabId(slab._id)
    setShowSlabForm(true)
  }

  const handleSaveSlab = () => {
    const { slabType, startDate, endDate, perDayAmount, flatAmount } = slabForm

    if (!startDate) {
      toast.error("Start date is required")
      return
    }

    if (slabType === "flat") {
      if (!endDate) {
        toast.error("End date is required for flat slabs")
        return
      }
      if (!flatAmount || flatAmount < 0) {
        toast.error("Flat amount is required and must be non-negative")
        return
      }
    } else if (slabType === "per_day") {
      if (!perDayAmount || perDayAmount < 0) {
        toast.error("Per day amount is required and must be non-negative")
        return
      }
    }

    const newSlab = {
      slabType,
      startDate: new Date(startDate),
      endDate: endDate ? new Date(endDate) : null,
      perDayAmount: slabType === "per_day" ? Number(perDayAmount) : undefined,
      flatAmount: slabType === "flat" ? Number(flatAmount) : undefined,
      isActive: true,
      _id: editingSlabId || `temp-${Date.now()}`
    }

    if (editingSlabId) {
      setSlabs(prev => prev.map(s => s._id === editingSlabId ? newSlab : s))
    } else {
      setSlabs(prev => [...prev, newSlab])
    }

    setShowSlabForm(false)
    setSlabForm({
      slabType: "flat",
      startDate: "",
      endDate: "",
      perDayAmount: "",
      flatAmount: "",
    })
  }

  const handleDeleteSlab = (slabId) => {
    setSlabs(prev => prev.filter(s => s._id !== slabId))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Late Fee Configuration - {className} ({quarter?.toUpperCase() || ''})
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-4">
          {classId && quarter && (
            isLoadingStatus ? (
              <p className="text-sm text-gray-500">Checking active late fee rule...</p>
            ) : config.isEnabled ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                <span className="font-semibold">Active rule:</span> Custom override for {className} ({quarter?.toUpperCase()}) — {describePolicyAmount(config)}
              </div>
            ) : globalLateFeePolicy?.isEnabled ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <span className="font-semibold">Active rule:</span> School default — {describePolicyAmount(globalLateFeePolicy)}. Enabling "Enable late fee" below will create a custom override for {className} ({quarter?.toUpperCase()}).
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <span className="font-semibold">No active rule:</span> No late fee policy is currently enabled for {className} ({quarter?.toUpperCase()}).
              </div>
            )
          )}

          {classId && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="applyToAll"
                  checked={applyToAllClasses}
                  onCheckedChange={(checked) => {
                    setApplyToAllClasses(checked)
                    if (checked) {
                      toast.info(`This configuration will be applied to all ${allClasses.length} classes for ${quarter?.toUpperCase()}`)
                    }
                  }}
                />
                <Label htmlFor="applyToAll" className="text-base font-normal cursor-pointer">
                  Apply to all classes for this quarter
                </Label>
              </div>
              {applyToAllClasses && (
                <p className="text-xs text-blue-700 mt-2 ml-6">
                  This will apply the same late fee settings to all {allClasses.length} classes for {quarter?.toUpperCase()}
                </p>
              )}
            </div>
          )}

          <div className="flex items-center space-x-2">
            <Checkbox
              id="enableLateFee"
              checked={config.isEnabled}
              onCheckedChange={(checked) =>
                setConfig({ ...config, isEnabled: checked })
              }
            />
            <Label htmlFor="enableLateFee" className="text-base font-normal cursor-pointer">
              Enable late fee {applyToAllClasses ? "for all classes" : "for this class"} and quarter
            </Label>
          </div>

          <div className="space-y-2">
            <Label>Calculation Type</Label>
            {settingsCalculationType == null ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : (
              <RadioGroup
                value={config.calculationType}
                onValueChange={(value) => setConfig({ ...config, calculationType: value })}
              >
                {settingsCalculationType === "per_day" && (
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="per_day" id="per_day" />
                    <Label htmlFor="per_day" className="font-normal cursor-pointer">
                      Per Day
                    </Label>
                  </div>
                )}
                {settingsCalculationType === "flat_by_slab" && (
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="flat_by_slab" id="flat_by_slab" />
                    <Label htmlFor="flat_by_slab" className="font-normal cursor-pointer">
                      Slab Based
                    </Label>
                  </div>
                )}
              </RadioGroup>
            )}
          </div>

          {config.calculationType === "per_day" && (
            <div className="space-y-2">
              <Label htmlFor="dailyLateFees">Per Day Amount (₹)</Label>
              <Input
                id="dailyLateFees"
                type="number"
                min="0"
                value={config.dailyLateFees}
                onChange={(e) =>
                  setConfig({ ...config, dailyLateFees: parseFloat(e.target.value) || 0 })
                }
                placeholder="Enter daily late fee amount"
              />
            </div>
          )}

          {config.calculationType === "flat_by_slab" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Fee Slabs</Label>
                <Button
                  onClick={handleAddSlab}
                  size="sm"
                  className="bg-[#0263cc] text-white hover:bg-[#0263cc]/90"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Slab
                </Button>
              </div>

              {slabs.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Type</TableHead>
                        <TableHead>Start Date</TableHead>
                        <TableHead>End Date</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {slabs.map((slab) => (
                        <TableRow key={slab._id}>
                          <TableCell className="font-medium capitalize">
                            {slab.slabType === "per_day" ? "Per Day" : "Flat"}
                          </TableCell>
                          <TableCell>
                            {new Date(slab.startDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {slab.endDate ? new Date(slab.endDate).toLocaleDateString() : "∞"}
                          </TableCell>
                          <TableCell>
                            ₹{slab.slabType === "per_day" ? slab.perDayAmount : slab.flatAmount}
                            {slab.slabType === "per_day" && "/day"}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditSlab(slab)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteSlab(slab._id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Slab Form Dialog */}
              {showSlabForm && (
                <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                  <h4 className="font-semibold">
                    {editingSlabId ? "Edit Slab" : "Add Slab"}
                  </h4>
                  
                  <div className="space-y-2">
                    <Label>Slab Type</Label>
                    <RadioGroup
                      value={slabForm.slabType}
                      onValueChange={(value) =>
                        setSlabForm({ ...slabForm, slabType: value })
                      }
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="flat" id="slab-flat" />
                        <Label htmlFor="slab-flat" className="font-normal cursor-pointer">
                          Flat Amount
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="per_day" id="slab-per-day" />
                        <Label htmlFor="slab-per-day" className="font-normal cursor-pointer">
                          Per Day Amount
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={slabForm.startDate}
                        onChange={(e) =>
                          setSlabForm({ ...slabForm, startDate: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>
                        End Date {slabForm.slabType === "per_day" && "(Optional)"}
                      </Label>
                      <Input
                        type="date"
                        value={slabForm.endDate}
                        onChange={(e) =>
                          setSlabForm({ ...slabForm, endDate: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  {slabForm.slabType === "flat" && (
                    <div className="space-y-2">
                      <Label>Flat Amount (₹)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={slabForm.flatAmount}
                        onChange={(e) =>
                          setSlabForm({ ...slabForm, flatAmount: e.target.value })
                        }
                        placeholder="Enter flat amount"
                      />
                    </div>
                  )}

                  {slabForm.slabType === "per_day" && (
                    <div className="space-y-2">
                      <Label>Per Day Amount (₹)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={slabForm.perDayAmount}
                        onChange={(e) =>
                          setSlabForm({ ...slabForm, perDayAmount: e.target.value })
                        }
                        placeholder="Enter per day amount"
                      />
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      onClick={handleSaveSlab}
                      className="bg-[#0263cc] text-white hover:bg-[#0263cc]/90"
                    >
                      {editingSlabId ? "Update" : "Add"} Slab
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowSlabForm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false)
              setApplyToAllClasses(false)
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading}
            className="bg-[#0263cc] text-white hover:bg-[#0263cc]/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading 
              ? (applyToAllClasses ? `Applying to ${allClasses.length} classes...` : "Saving...") 
              : (applyToAllClasses ? `Apply to All Classes (${allClasses.length})` : "Save Configuration")
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default LateFeeConfigDialog

